import { action, makeObservable } from 'mobx';
import { IkeDrawBindings } from '../../bindings';
import type { IkeDraw } from '../../draw';
import { IkeDrawTool } from '../../draw.state';
import { IkeDrawLabel } from '../../drawable/util.draw.svg';
import type { SVGEventContext } from './svg.event.context';
import { SVGEventTranslation } from './svg.event.translation';
import { SVGMove } from './svg.move';
import type { SVGRenderOptions } from './svg.render';

const MAX_CLICK_MOVEMENT = 15;

export class SVGEventHandler {
	dc: IkeDraw;
	lastRenderOptions: SVGRenderOptions | null = null;

	constructor(
		dc: IkeDraw,
		private onStartHOASession?: () => void
	) {
		makeObservable(this);
		this.dc = dc;
	}

	static getStructure(evt: MouseEvent): SVGEventContext {
		const output: SVGEventContext = {
			id: null,
			label: null,
			tag: null,
			draggable: false,
			point: null,
			$event: null,
		};

		if (evt.target == null) {
			return output;
		}

		let current: HTMLElement = <HTMLElement>evt.target;
		let count = 0;
		while (current.parentNode != null) {
			const nodeId = current.getAttribute('c-Draw-node-id');
			if (output.id == null && nodeId != null) {
				output.id = nodeId;
				output.tag = current.getAttribute('c-Draw-tag');
			}

			const labelId = current.getAttribute('c-Draw-Label');
			if (labelId != null && output.label == null) {
				output.label = labelId as IkeDrawLabel;

				if (
					current.getAttribute('c-Draw-draggable') === 'true' ||
					<any>current.getAttribute('c-Draw-draggable') === true
				) {
					output.draggable = true;
				}
			}

			// Label will be present before the node-id
			if (output.id && (output.label || count > 0)) {
				return output;
			}

			current = <HTMLElement>current.parentNode;
			count++;
		}
		return output;
	}

	@action.bound click($event: MouseEvent) {
		const dc = this.dc;
		const state = dc.state;
		const clickX = state.mouseDownX;
		const clickY = state.mouseDownY;
		if (clickX != null) {
			const diffXY = Math.abs(clickX - $event.clientX) + Math.abs(clickY - $event.clientY);
			if (diffXY > MAX_CLICK_MOVEMENT) {
				return;
			}
		}

		const ctx = SVGEventHandler.getStructure($event);
		const drawableId = ctx.label === IkeDrawLabel.LABEL && ctx.id ? ctx.id : undefined;
		const center = SVGEventTranslation.translateEvent($event);
		const tool = state.tool;

		if (this.dc.state.linkFieldIds.length > 0) {
			this.onStartHOASession?.();
			IkeDrawBindings.Measurement.Marker.create(dc, { drawableId, center, $event });

			if (dc.state.autoArrangeAnnotations && this.lastRenderOptions) {
				dc.state.arrangeAnnotations(this.lastRenderOptions);
			}
			return;
		}

		if (ctx.label === IkeDrawLabel.DELETE) {
			this.onStartHOASession?.();
			IkeDrawBindings.Measurement.delete(dc, { drawableId: ctx.id, $event });
			return;
		}

		if (ctx.label === IkeDrawLabel.ACCEPT) {
			this.onStartHOASession?.();
			IkeDrawBindings.Measurement.accept(dc, { drawableId: ctx.id, $event });
			dc.state.setAutoArrangeAnnotations(dc.drawState.hasOnlyInsightDetections());
			return;
		}

		if (tool === IkeDrawTool.BASE || tool === IkeDrawTool.MARKER) {
			// Note that the UI currently forces the tool to be BASE until a base exists,
			// which means if the tool is MARKER, then a base must already be set.

			const highlightedId = state.hoverId;

			// If clicking on an approvable insight drawable, then approve it.
			if (highlightedId && highlightedId !== 'image') {
				if (!dc.drawState.hasDrawable(highlightedId)) {
					return;
				}

				const drawable = dc.drawState.getDrawable(highlightedId);

				if (drawable.isApprovableInsightDrawable() && drawable.isInsightBaseOffsetDetectionClass) {
					this.onStartHOASession?.();
					drawable.approveInsightFlag();
				}
			} else {
				// User is clicking on empty space, so create a new base or marker.

				this.onStartHOASession?.();
				IkeDrawBindings.Measurement.Marker.create(dc, { drawableId, center, $event });
				if (dc.state.autoArrangeAnnotations && this.lastRenderOptions) {
					dc.state.arrangeAnnotations(this.lastRenderOptions);
				}
			}
			return;
		}

		if (tool === IkeDrawTool.LINE) {
			IkeDrawBindings.Measurement.Line.create(dc, { drawableId, center, $event });
			if (dc.state.autoArrangeAnnotations && this.lastRenderOptions) {
				dc.state.arrangeAnnotations(this.lastRenderOptions);
			}
			dc.state.toggleTool(IkeDrawTool.LINE);
			return;
		}
	}

	@action.bound doubleclick($event: MouseEvent) {
		const ctx: SVGEventContext = SVGEventHandler.getStructure($event);

		if (ctx.id == null || ctx.label !== IkeDrawLabel.LABEL) {
			return;
		}

		const dc = this.dc;
		const state = dc.state;
		state.propertiesShow(ctx.id);
	}

	@action.bound mousedown(evt: MouseEvent) {
		const ctx = SVGEventHandler.getStructure(evt);
		const dc = this.dc;

		const state = dc.state;

		state.mouseDownX = evt.clientX;
		state.mouseDownY = evt.clientY;

		if (ctx.id && evt.which === 1 && ctx.tag !== 'image') {
			const object = dc.drawState.getDrawable(ctx.id);
			if (object == null) {
				return;
			}

			if (ctx.label == null) {
				dc.state.setSelected(null, null);
			} else {
				// If the base tool is selected, then we don't want to be able to select any other drawable
				if (state.tool !== IkeDrawTool.BASE) {
					dc.state.setSelected(ctx.id, ctx.label);
				} else {
					const drawable = dc.drawState.getDrawable(ctx.id);
					if (drawable.isApprovableInsightDrawable() && drawable.isInsightBaseOffsetDetectionClass) {
						dc.state.setSelected(ctx.id, ctx.label);
					}
				}
			}
		} else {
			dc.state.setSelected(null, null);
		}

		if (ctx.id == null || ctx.draggable !== true) {
			return;
		}
		ctx.$event = evt;
		// Start listening for move events.
		const mover = SVGMove.create(this.dc, ctx);
		if (mover == null) {
			return;
		}
		state.moveId = ctx.id;

		mover.onMove = () => {
			IkeDrawBindings.Selection.move(dc, {
				label: ctx.label,
				delta: mover.delta,
				$event: null,
			});

			if (ctx.label === IkeDrawLabel.LABEL) {
				dc.state.setAutoArrangeAnnotations(false);
			}
		};
		mover.onStart = () => {
			this.onStartHOASession?.();
		};
		mover.start();

		mover.onEnd = () => {
			if (dc.state.autoArrangeAnnotations && ctx.label !== IkeDrawLabel.LABEL && this.lastRenderOptions) {
				dc.state.arrangeAnnotations(this.lastRenderOptions);
			}
		};
	}

	@action.bound mousemove(evt: MouseEvent) {
		const ctx = SVGEventHandler.getStructure(evt);
		const state = this.dc.state;

		if (ctx.id == null) {
			state.setHovered(null);
			return;
		}

		// If the base tool is selected, then we don't want to be able to select any other drawable
		if (state.tool === IkeDrawTool.BASE) {
			const drawable = this.dc.drawState.getDrawable(ctx.id);
			if (drawable.isApprovableInsightDrawable() && !drawable.isInsightBaseOffsetDetectionClass) {
				state.setHovered(null);
				return;
			}
		}

		const highlightedId = state.hoverId;
		const currentMove = state.moveId;
		if (currentMove != null) {
			return;
		}

		if ((highlightedId == null && ctx.id == null) || (highlightedId && ctx.id === highlightedId)) {
			// Not moving and the highlighted ID is the same
			return;
		}

		state.setHovered(ctx.id);
	}

	@action.bound mousewheel(delta: number, $event: WheelEvent) {
		const dc = this.dc;
		IkeDrawBindings.Image.zoom(dc, {
			delta,
			center: SVGEventTranslation.translateEvent($event),
			$event,
		});
	}
}
